import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { AuthService } from '@/effects/auth/auth.service';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { STORAGE_KEYS } from '@/lib/constants';
import { logger } from '@/lib/logger';
import type {
  User,
  LoginCredentials,
  RegisterData,
  AuthResponse,
} from '@/api/schemas/user.schema';

// Extended login credentials to include OTP
export interface LoginWithOTP extends LoginCredentials {
  otp: string;
}

export interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials | LoginWithOTP) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  refreshTokens: () => Promise<string>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get): AuthState => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login action
      login: async (credentials) => {
        set({ isLoading: true, error: null });

        try {
          const program = pipe(
            AuthService,
            Effect.flatMap((service) => service.login(credentials)),
            Effect.provide(MainLayer)
          );

          const result: AuthResponse = await runEffect(program);

          set({
            user: result.user,
            accessToken: result.accessToken,
            refreshToken: result.refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      // Register action
      register: async (data) => {
        set({ isLoading: true, error: null });

        try {
          const program = pipe(
            AuthService,
            Effect.flatMap((service) => service.register(data)),
            Effect.provide(MainLayer)
          );

          const result: AuthResponse = await runEffect(program);

          set({
            user: result.user,
            accessToken: result.accessToken,
            refreshToken: result.refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Registration failed';
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      // Logout action
      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });

        // Clear other stores if needed
        window.dispatchEvent(new CustomEvent('auth:logout'));
      },

      // Clear error action
      clearError: () => set({ error: null }),

      // Set tokens (for manual token updates)
      setTokens: (accessToken, refreshToken) => {
        const currentState = get();
        set({
          accessToken,
          refreshToken: refreshToken || currentState.refreshToken,
        });
      },

      // Refresh tokens
      refreshTokens: async () => {
        const currentState = get();

        if (!currentState.refreshToken) {
          throw new Error('No refresh token available');
        }

        try {
          const program = pipe(
            AuthService,
            Effect.flatMap((service) =>
              service.refreshToken(currentState.refreshToken!)
            ),
            Effect.provide(MainLayer)
          );

          const newAccessToken = await runEffect(program);

          set({
            accessToken: newAccessToken,
            error: null,
          });

          return newAccessToken;
        } catch (error) {
          // If refresh fails, logout the user
          get().logout();
          throw error;
        }
      },
    }),
    {
      name: STORAGE_KEYS.AUTH_TOKEN,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Verify tokens are still valid on rehydration
        if (state?.accessToken && state?.isAuthenticated) {
          // Optional: Add token validation logic here
          logger.debug('Auth state rehydrated');
        }
      },
    }
  )
);

// Token refresh queue to prevent concurrent refresh requests
let refreshPromise: Promise<string> | null = null;

// Auth store implementation for the API client
export const AuthStoreLive = {
  getToken: async (): Promise<string | null> => {
    const state = useAuthStore.getState();
    return state.accessToken;
  },

  refreshToken: async (): Promise<string> => {
    // If there's already a refresh in progress, wait for it
    if (refreshPromise) {
      logger.debug('Token refresh already in progress, waiting for existing refresh');
      return refreshPromise;
    }

    // Start a new refresh
    logger.debug('Starting token refresh');
    refreshPromise = useAuthStore.getState().refreshTokens()
      .finally(() => {
        // Clear the promise once refresh is complete (success or failure)
        refreshPromise = null;
      });

    return refreshPromise;
  },

  clearAuth: (): void => {
    const state = useAuthStore.getState();
    state.logout();
  },

  setTokens: (accessToken: string, refreshToken?: string): void => {
    const state = useAuthStore.getState();
    state.setTokens(accessToken, refreshToken);
  },
};

// Selectors for easier state access
export const useCurrentUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);

// Auth actions
export const useAuthActions = () => {
  const store = useAuthStore();
  return {
    login: store.login,
    register: store.register,
    logout: store.logout,
    clearError: store.clearError,
  };
};